/**
 * Unified Auth Provider Abstraction
 *
 * This module provides a unified interface for authentication that works with either Okta or MS Entra.
 * Only ONE provider should be configured per deployment. If both are configured, the system will
 * throw an error during initialization.
 */

import {
	getMsEntraToken,
	getMsEntraUser,
	getValidMsEntraAccessToken,
	handleMsEntraCallback,
	isMsEntraAuthenticated,
	isMsEntraEnabled,
	loginWithMsEntra,
	logoutFromMsEntra,
} from './msEntraAuth';
import {
	getOktaAuth,
	getOktaUser,
	getValidOktaAccessToken,
	handleOktaCallback,
	isOktaAuthenticated,
	isOktaEnabled,
	loginWithOkta,
	logoutFromOkta,
} from './oktaAuth';

// Validation: Check that only one provider is configured
function validateAuthProviderConfiguration(): void {
	const oktaConfigured = isOktaEnabled();
	const msEntraConfigured = isMsEntraEnabled();

	if (oktaConfigured && msEntraConfigured) {
		throw new Error(
			'Both Okta and MS Entra are configured, but only one SSO provider is allowed per deployment. ' +
				'Please configure only one of OKTA_ISSUER or MS_ENTRA_CLIENT_ID.',
		);
	}
}

// Type definitions for the unified auth interface
export interface AuthUser {
	name?: string;
	preferred_username?: string;
	email?: string;
	sub?: string;
	picture?: string;
	[key: string]: unknown; // Allow additional claims
}

export interface AuthProvider {
	// Provider type
	type: 'okta' | 'ms-entra' | 'none';

	// Capabilities
	isEnabled: boolean;
	isAuthenticated: () => Promise<boolean>;
	login: () => Promise<void>;
	handleCallback: () => Promise<string | null>;
	getUser: () => Promise<AuthUser>;
	getAccessToken: () => Promise<string | null>;
	getValidAccessToken: () => Promise<string | null>;
	logout: () => Promise<void>;
}

// Determine which provider is configured
function detectProviderType(): 'okta' | 'ms-entra' | 'none' {
	if (isOktaEnabled()) {
		return 'okta';
	} else if (isMsEntraEnabled()) {
		return 'ms-entra';
	}
	return 'none';
}

// Create the appropriate provider instance
function createProvider(): AuthProvider {
	const providerType = detectProviderType();

	// Validate configuration (this will throw if both are configured)
	validateAuthProviderConfiguration();

	switch (providerType) {
		case 'okta':
			return {
				type: 'okta',
				isEnabled: true,
				isAuthenticated: isOktaAuthenticated,
				login: loginWithOkta,
				handleCallback: async () => {
					const tokens = await handleOktaCallback();
					return tokens?.accessToken?.accessToken ?? null;
				},
				getUser: getOktaUser,
				getAccessToken: async () => {
					const auth = getOktaAuth();
					if (!auth) return null;
					try {
						return (await auth.getAccessToken()) ?? null;
					} catch (error) {
						console.error('Error getting Okta access token:', error);
						return null;
					}
				},
				getValidAccessToken: getValidOktaAccessToken,
				logout: logoutFromOkta,
			};

		case 'ms-entra':
			return {
				type: 'ms-entra',
				isEnabled: true,
				isAuthenticated: isMsEntraAuthenticated,
				login: loginWithMsEntra,
				handleCallback: handleMsEntraCallback,
				getUser: getMsEntraUser,
				getAccessToken: getMsEntraToken,
				getValidAccessToken: getValidMsEntraAccessToken,
				logout: logoutFromMsEntra,
			};

		default:
			return {
				type: 'none',
				isEnabled: false,
				isAuthenticated: async () => false,
				login: async () => {
					throw new Error('No SSO provider configured');
				},
				handleCallback: async () => null,
				getUser: async () => ({}),
				getAccessToken: async () => null,
				getValidAccessToken: async () => null,
				logout: async () => {},
			};
	}
}

// Singleton provider instance
let authProvider: AuthProvider | null = null;

/**
 * Get the current auth provider instance
 * This validates the configuration on first access
 */
export function getAuthProvider(): AuthProvider {
	if (!authProvider) {
		validateAuthProviderConfiguration();
		authProvider = createProvider();
	}
	return authProvider;
}

/**
 * Check if any SSO provider is enabled
 */
export function isAuthEnabled(): boolean {
	return isOktaEnabled() || isMsEntraEnabled();
}

/**
 * Get the current provider type
 */
export function getProviderType(): 'okta' | 'ms-entra' | 'none' {
	return detectProviderType();
}

/**
 * Check if current provider is Okta
 */
export function isUsingOkta(): boolean {
	return detectProviderType() === 'okta';
}

/**
 * Check if current provider is MS Entra
 */
export function isUsingMsEntra(): boolean {
	return detectProviderType() === 'ms-entra';
}

// Re-export provider-specific functions for convenience (these will only work if the respective provider is configured)

/**
 * Check if user is authenticated (uses configured provider)
 */
export const isAuthenticated = async (): Promise<boolean> => {
	const provider = getAuthProvider();
	return provider.isAuthenticated();
};

/**
 * Login using configured provider
 */
export const login = async (): Promise<void> => {
	const provider = getAuthProvider();
	return provider.login();
};

/**
 * Handle authentication callback (uses configured provider)
 */
export const handleCallback = async (): Promise<string | null> => {
	const provider = getAuthProvider();
	return provider.handleCallback();
};

/**
 * Get user information (uses configured provider)
 */
export const getUser = async (): Promise<AuthUser> => {
	const provider = getAuthProvider();
	return provider.getUser();
};

/**
 * Get access token (uses configured provider)
 */
export const getAccessToken = async (): Promise<string | null> => {
	const provider = getAuthProvider();
	return provider.getAccessToken();
};

/**
 * Get valid access token (uses configured provider)
 */
export const getValidAccessToken = async (): Promise<string | null> => {
	const provider = getAuthProvider();
	return provider.getValidAccessToken();
};

/**
 * Logout (uses configured provider)
 */
export const logout = async (): Promise<void> => {
	const provider = getAuthProvider();
	return provider.logout();
};

/**
 * Reset the provider instance (useful for testing)
 */
export function resetAuthProvider(): void {
	authProvider = null;
}

// Export validation function for use during app initialization
export { validateAuthProviderConfiguration };
